<script setup>
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AuthShell from '@/components/auth/AuthShell.vue';
import AuthField from '@/components/auth/AuthField.vue';
import UiButton from '@/components/ui/UiButton.vue';
import { useAuthStore } from '@/store/auth/index.js';
import { usePageMeta } from '@/composables/usePageMeta';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

// One sign-in for both doors: /login for the Deenly team, /akademija/prijava for the Academy.
const academy = computed(() => Boolean(route.meta.academy));
usePageMeta(() =>
  academy.value
    ? { title: t('study.auth.login.metaTitleAcademy'), description: t('study.auth.login.metaDescAcademy'), path: '/akademija/prijava', lang: locale.value }
    : { title: t('study.auth.login.metaTitle'), description: t('study.auth.login.metaDesc'), path: '/login', lang: locale.value }
);

const form = reactive({ email: typeof route.query.email === 'string' ? route.query.email : '', password: '' });
const loading = ref(false);
const failure = ref(null);
const lockedUntil = ref(0);
const now = ref(Date.now());
const passwordField = ref(null);
let ticker = null;

const shell = computed(() =>
  academy.value
    ? { badge: t('study.common.brand'), title: t('study.auth.login.academy.title'), accent: t('study.auth.login.academy.accent'), lead: t('study.auth.login.academy.lead'), cardTitle: t('study.auth.login.academy.cardTitle'), home: '/akademija', homeLabel: t('study.common.academy') }
    : { badge: t('study.auth.panelBadge'), homeLabel: t('study.auth.backToSite'), title: t('study.auth.login.panel.title'), accent: t('study.auth.login.panel.accent'), lead: t('study.auth.login.panel.lead'), cardTitle: t('study.auth.login.panel.cardTitle') }
);

// Back to the page that sent the person here — only somewhere on this site; else their own home.
const destination = () => {
  const redirect = route.query.redirect;
  return typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : auth.homeRoute;
};

const secondsLeft = computed(() => Math.max(0, Math.ceil((lockedUntil.value - now.value) / 1000)));
const locked = computed(() => secondsLeft.value > 0);
const countdown = computed(() => `${Math.floor(secondsLeft.value / 60)}:${String(secondsLeft.value % 60).padStart(2, '0')}`);

const message = computed(() => {
  if (locked.value) return t('study.auth.login.locked', { time: countdown.value });
  return {
    credentials: t('study.auth.login.errors.credentials'),
    invalid: t('study.auth.login.errors.invalid'),
    network: t('study.auth.login.errors.network'),
    server: t('study.auth.login.errors.server'),
  }[failure.value] ?? '';
});

const notice = computed(() => ({ reset: t('study.auth.login.notices.reset'), expired: t('study.auth.login.notices.expired') })[route.query.notice] ?? '');

function startLock(seconds) {
  lockedUntil.value = Date.now() + seconds * 1000;
  clearInterval(ticker);
  ticker = setInterval(() => {
    now.value = Date.now();
    if (!locked.value) clearInterval(ticker);
  }, 1000);
}
onBeforeUnmount(() => clearInterval(ticker));

async function submit() {
  if (loading.value || locked.value) return;
  failure.value = null;
  loading.value = true;
  const result = await auth.login(form);
  loading.value = false;

  if (result.ok) {
    router.replace(destination());
    return;
  }
  failure.value = result.reason;
  if (result.reason === 'locked') startLock(result.retryAfter);
  if (result.reason === 'credentials') {
    form.password = '';
    passwordField.value?.focus();
  }
}
</script>

<template>
  <AuthShell v-bind="shell">
    <template #aside>
      <p v-if="academy" class="mt-8 text-sm text-deen-ink/55 max-w-sm">
        {{ t('study.auth.login.noAccount') }}
        <RouterLink :to="{ name: 'academy.signup', query: route.query.redirect ? { redirect: route.query.redirect } : {} }" class="font-semibold text-deen-ink underline underline-offset-4">{{ t('study.auth.login.createFree') }}</RouterLink>
      </p>
      <p v-else class="mt-8 flex items-start gap-2.5 text-sm text-deen-ink/50 max-w-sm">
        <iconify-icon icon="solar:shield-keyhole-linear" class="text-lg text-deen-ink/40 shrink-0" aria-hidden="true" />
        {{ t('study.auth.login.teamOnly') }}
      </p>
    </template>
    <form class="space-y-5" novalidate @submit.prevent="submit">
      <div v-if="notice && !message" class="flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" role="status">
        <iconify-icon icon="solar:check-circle-linear" class="text-lg shrink-0 mt-px" aria-hidden="true" />{{ notice }}
      </div>
      <div v-if="message" :class="['flex items-start gap-3 rounded-2xl px-4 py-3 text-sm border', locked ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-800']" role="alert">
        <iconify-icon :icon="locked ? 'solar:shield-warning-linear' : 'solar:danger-circle-linear'" class="text-lg shrink-0 mt-px" aria-hidden="true" />
        <span>{{ message }}</span>
      </div>

      <AuthField v-model="form.email" :label="t('study.common.email')" type="email" autocomplete="username" required />
      <AuthField ref="passwordField" v-model="form.password" :label="t('study.common.password')" type="password" autocomplete="current-password" required>
        <template #aside>
          <RouterLink :to="{ name: 'auth.forgot', query: form.email ? { email: form.email } : {} }" class="text-[13px] 4xl:text-sm text-deen-ink/55 hover:text-deen-ink underline-offset-4 hover:underline">
            {{ t('study.auth.login.forgot') }}
          </RouterLink>
        </template>
      </AuthField>

      <UiButton type="submit" size="lg" block class="!mt-7 4xl:h-14 4xl:text-base" :loading="loading" :class="locked || !form.email || !form.password ? 'opacity-40 pointer-events-none' : ''" :aria-disabled="locked || !form.email || !form.password">
        {{ locked ? t('study.auth.login.retryIn', { time: countdown }) : t('study.auth.login.submit') }}
      </UiButton>
    </form>

  </AuthShell>
</template>
